import React, { useEffect, useRef, useState } from "react";
import "./cloud-login.css";
import cloudHeroPoster from "./cloud-hero-poster.jpg";

// --- Precision SVG Icons ---
const Icon = ({ name, size = 16, className = "" }) => {
  const icons = {
    mail: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" />
      </svg>
    ),
    lock: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    eye: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
      </svg>
    ),
    eyeOff: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ),
    globe: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    arrowRight: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
      </svg>
    ),
    shieldCheck: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" />
      </svg>
    ),
    qrCode: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    building: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="9" y1="22" x2="9" y2="12" /><line x1="15" y1="22" x2="15" y2="12" /><line x1="9" y1="6" x2="9.01" y2="6" /><line x1="15" y1="6" x2="15.01" y2="6" /><line x1="9" y1="10" x2="9.01" y2="10" /><line x1="15" y1="10" x2="15.01" y2="10" />
      </svg>
    ),
    info: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
    chevronLeft: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="15 18 9 12 15 6" />
      </svg>
    ),
  };
  return icons[name] || null;
};

const QuantumField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = canvas?.closest(".cloud-stage-hero");
    if (!canvas || !stage) return undefined;

    const context = canvas.getContext("2d");
    if (!context) return undefined;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const palette = [194, 211, 232, 260, 282];
    const pointer = { x: 0, y: 0, targetX: 0, targetY: 0, active: false };
    const ripples = [];
    let nodes = [];
    let width = 0;
    let height = 0;
    let frame = 0;
    let lastRippleAt = 0;
    let randomSeed = 0x6d2b79f5;

    const random = () => {
      randomSeed = (Math.imul(randomSeed, 1664525) + 1013904223) >>> 0;
      return randomSeed / 4294967296;
    };

    const buildNodes = () => {
      randomSeed = 0x6d2b79f5;
      const count = Math.max(28, Math.min(54, Math.round((width * height) / 17000)));
      nodes = Array.from({ length: count }, (_, index) => ({
        nx: 0.04 + random() * 0.92,
        ny: 0.08 + random() * 0.84,
        depth: 0.35 + random() * 0.75,
        radius: 0.65 + random() * 1.25,
        phase: random() * Math.PI * 2,
        speed: 0.00045 + random() * 0.00055,
        hue: palette[index % palette.length],
      }));
    };

    const resize = () => {
      const bounds = stage.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 1.6);
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      buildNodes();
    };

    const positionNode = (node, time) => ({
      x: node.nx * width + pointer.x * node.depth * 15 + Math.sin(time * node.speed + node.phase) * 5,
      y: node.ny * height + pointer.y * node.depth * 11 + Math.cos(time * node.speed * 0.82 + node.phase) * 4,
    });

    const draw = (time = 0) => {
      context.clearRect(0, 0, width, height);
      pointer.x += (pointer.targetX - pointer.x) * 0.065;
      pointer.y += (pointer.targetY - pointer.y) * 0.065;

      stage.style.setProperty("--stage-shift-x", `${(-pointer.x * 10).toFixed(2)}px`);
      stage.style.setProperty("--stage-shift-y", `${(-pointer.y * 7).toFixed(2)}px`);

      const positions = nodes.map((node) => positionNode(node, time));
      context.save();
      context.globalCompositeOperation = "lighter";

      for (let index = 0; index < positions.length; index += 1) {
        for (let next = index + 1; next < positions.length; next += 1) {
          const from = positions[index];
          const to = positions[next];
          const distance = Math.hypot(from.x - to.x, from.y - to.y);
          const threshold = 82 + nodes[index].depth * 54;
          if (distance > threshold) continue;
          const alpha = (1 - distance / threshold) * 0.12;
          const gradient = context.createLinearGradient(from.x, from.y, to.x, to.y);
          gradient.addColorStop(0, `hsla(${nodes[index].hue}, 92%, 68%, ${alpha})`);
          gradient.addColorStop(1, `hsla(${nodes[next].hue}, 92%, 68%, 0)`);
          context.strokeStyle = gradient;
          context.lineWidth = 0.65;
          context.beginPath();
          context.moveTo(from.x, from.y);
          context.lineTo(to.x, to.y);
          context.stroke();
        }
      }

      positions.forEach((position, index) => {
        const node = nodes[index];
        const shimmer = 0.55 + Math.sin(time * node.speed * 1.7 + node.phase) * 0.45;
        const radius = node.radius + shimmer * 0.7;
        const glow = context.createRadialGradient(position.x, position.y, 0, position.x, position.y, radius * 7);
        glow.addColorStop(0, `hsla(${node.hue}, 100%, 82%, ${0.78 + shimmer * 0.18})`);
        glow.addColorStop(0.2, `hsla(${node.hue}, 96%, 66%, ${0.35 + shimmer * 0.2})`);
        glow.addColorStop(1, `hsla(${node.hue}, 90%, 55%, 0)`);
        context.fillStyle = glow;
        context.beginPath();
        context.arc(position.x, position.y, radius * 7, 0, Math.PI * 2);
        context.fill();

        if (pointer.active) {
          const pointerX = ((pointer.targetX + 1) / 2) * width;
          const pointerY = ((pointer.targetY + 1) / 2) * height;
          const distance = Math.hypot(position.x - pointerX, position.y - pointerY);
          if (distance < 175) {
            context.strokeStyle = `rgba(116, 219, 255, ${(1 - distance / 175) * 0.28})`;
            context.lineWidth = 0.8;
            context.beginPath();
            context.moveTo(position.x, position.y);
            context.lineTo(pointerX, pointerY);
            context.stroke();
          }
        }
      });

      const centerX = width * 0.53 + pointer.x * 9;
      const centerY = height * 0.47 + pointer.y * 7;
      [0, 1, 2].forEach((orbit) => {
        const angle = time * (0.0001 + orbit * 0.000025) + orbit * 2.1;
        const radiusX = width * (0.19 + orbit * 0.035);
        const radiusY = height * (0.115 + orbit * 0.02);
        const x = centerX + Math.cos(angle) * radiusX;
        const y = centerY + Math.sin(angle) * radiusY;
        const glow = context.createRadialGradient(x, y, 0, x, y, 14);
        glow.addColorStop(0, orbit === 1 ? "rgba(249, 211, 113, .92)" : "rgba(154, 232, 255, .95)");
        glow.addColorStop(0.24, orbit === 1 ? "rgba(232, 174, 61, .38)" : "rgba(85, 164, 255, .42)");
        glow.addColorStop(1, "rgba(40, 100, 255, 0)");
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, 14, 0, Math.PI * 2);
        context.fill();
      });

      for (let index = ripples.length - 1; index >= 0; index -= 1) {
        const ripple = ripples[index];
        const age = time - ripple.startedAt;
        if (age > 980) {
          ripples.splice(index, 1);
          continue;
        }
        const progress = age / 980;
        context.strokeStyle = `rgba(107, 213, 255, ${(1 - progress) * 0.24})`;
        context.lineWidth = 1.15 - progress * 0.55;
        context.beginPath();
        context.arc(ripple.x, ripple.y, 14 + progress * 76, 0, Math.PI * 2);
        context.stroke();
      }

      context.restore();
      if (!motionQuery.matches) frame = window.requestAnimationFrame(draw);
    };

    const handlePointerMove = (event) => {
      const bounds = stage.getBoundingClientRect();
      const localX = event.clientX - bounds.left;
      const localY = event.clientY - bounds.top;
      pointer.targetX = Math.max(-1, Math.min(1, (localX / bounds.width) * 2 - 1));
      pointer.targetY = Math.max(-1, Math.min(1, (localY / bounds.height) * 2 - 1));
      pointer.active = true;
      const now = performance.now();
      if (now - lastRippleAt > 105) {
        ripples.push({ x: localX, y: localY, startedAt: now });
        if (ripples.length > 6) ripples.shift();
        lastRippleAt = now;
      }
    };

    const handlePointerLeave = () => {
      pointer.targetX = 0;
      pointer.targetY = 0;
      pointer.active = false;
    };

    const handleMotionPreference = () => {
      window.cancelAnimationFrame(frame);
      if (motionQuery.matches) draw(performance.now());
      else frame = window.requestAnimationFrame(draw);
    };

    resize();
    stage.addEventListener("pointermove", handlePointerMove, { passive: true });
    stage.addEventListener("pointerleave", handlePointerLeave);
    window.addEventListener("resize", resize);
    motionQuery.addEventListener?.("change", handleMotionPreference);
    handleMotionPreference();

    return () => {
      window.cancelAnimationFrame(frame);
      stage.removeEventListener("pointermove", handlePointerMove);
      stage.removeEventListener("pointerleave", handlePointerLeave);
      window.removeEventListener("resize", resize);
      motionQuery.removeEventListener?.("change", handleMotionPreference);
      stage.style.removeProperty("--stage-shift-x");
      stage.style.removeProperty("--stage-shift-y");
    };
  }, []);

  return <canvas ref={canvasRef} className="cloud-stage-quantum-field" aria-hidden="true" />;
};

// --- Prestige Translations (Pure Visual & Functional, No Data Introductions) ---
const authI18n = {
  zh: {
    brandKicker: "ENTERPRISE CLOUD",
    stageTitle: "释放智能的无限潜能",
    stageSubtitle: "面向未来的企业级多模态算力与点播基础设施",
    backHome: "返回官网",
    langName: "EN / 英文",
    tabPassword: "账号密码登录",
    tabSso: "企业单点登录",
    welcome: "登录企业控制台",
    welcomeSub: "输入企业凭证以进入您的工作空间",
    emailLabel: "企业邮箱",
    emailPlaceholder: "name@company.com",
    passwordLabel: "登录密码",
    passwordPlaceholder: "输入账户密码",
    ssoDomainLabel: "企业组织域名",
    ssoDomainPlaceholder: "company.goldly.ai 或企业专属标识",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
    rememberMe: "在此设备上保持登录",
    forgotPassword: "忘记密码？",
    submitBtn: "登录企业控制台",
    submitSsoBtn: "验证并继续",
    orText: "或使用快捷方式",
    ssoBtn: "使用企业统一身份认证 (SAML 2.0)",
    noAccount: "还没有企业账户？",
    contactSales: "联系商务顾问开通专属席位 →",
    privacy: "隐私政策",
    terms: "服务条款",
    help: "帮助文档",
    copyright: "© 2026 GOLDLY AI GROUP. All rights reserved.",
    errEmail: "请输入有效的企业邮箱地址。",
    errPassword: "密码至少需要 6 个字符。",
    errSsoDomain: "请输入有效的企业组织标识或域名。",
    noticeComingSoon: "企业身份管理系统 (SSO/SAML) 正在同步，请使用企业邮箱与密码登录。",
  },
  en: {
    brandKicker: "ENTERPRISE CLOUD",
    stageTitle: "Unleash Boundless Intelligence",
    stageSubtitle: "Next-generation multimodal compute & VOD cloud infrastructure",
    backHome: "Back to Site",
    langName: "中 / 中文",
    tabPassword: "Password",
    tabSso: "Enterprise SSO",
    welcome: "Sign in to Console",
    welcomeSub: "Enter your credentials to access your workspace",
    emailLabel: "Work Email",
    emailPlaceholder: "name@company.com",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter account password",
    ssoDomainLabel: "Enterprise Workspace Domain",
    ssoDomainPlaceholder: "company.goldly.ai or organization ID",
    showPassword: "Show password",
    hidePassword: "Hide password",
    rememberMe: "Keep me signed in on this device",
    forgotPassword: "Forgot password?",
    submitBtn: "Sign In to Enterprise Console",
    orText: "or continue with",
    ssoBtn: "Sign in with Enterprise SSO (SAML 2.0)",
    submitSsoBtn: "Verify & Continue",
    noAccount: "Don't have an enterprise account?",
    contactSales: "Contact Enterprise Sales for Access →",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    help: "Documentation",
    copyright: "© 2026 GOLDLY AI GROUP. All rights reserved.",
    errEmail: "Please enter a valid work email address.",
    errPassword: "Password must be at least 6 characters.",
    errSsoDomain: "Please enter a valid organization identifier.",
    noticeComingSoon: "Enterprise SSO identity service is syncing. Please sign in with your work email and password.",
  },
};

export const CloudLogin = ({ onBack, onLogin }) => {
  const [lang, setLang] = useState("zh");
  const t = authI18n[lang];

  // Interactive Login Tabs (Rich product-grade functionality without messy data)
  const [activeTab, setActiveTab] = useState("password"); // "password" | "sso" | "qr"

  // Pre-filled credentials so testing is frictionless
  const [email, setEmail] = useState("admin@goldly.ai");
  const [password, setPassword] = useState("goldly2026");
  const [ssoDomain, setSsoDomain] = useState("corp.goldly.ai");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const previousTitle = document.title;
    document.title = lang === "zh" ? "登录 | GOLDLY AI 云控制台" : "Sign In | GOLDLY AI Cloud Console";
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
    };
  }, [lang]);

  const toggleLang = () => {
    setLang((prev) => (prev === "zh" ? "en" : "zh"));
  };

  const submitLogin = (event) => {
    event.preventDefault();
    setMessage("");

    if (activeTab === "sso") {
      if (!ssoDomain.trim()) {
        setMessage(t.errSsoDomain);
        return;
      }
      onLogin?.({ email: "admin@goldly.ai", password: "goldly2026", remember, sso: true });
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setMessage(t.errEmail);
      return;
    }

    if (password.length < 6) {
      setMessage(t.errPassword);
      return;
    }

    onLogin?.({ email: email.trim(), password, remember });
  };

  const showComingSoon = () => {
    setMessage(t.noticeComingSoon);
  };

  return (
    <main className="cloud-auth-screen">
      {/* =================================================================
          LEFT COLUMN: PURE VISUAL 3D KEY VISUAL (大厂纯视觉艺术展台，无数据无自夸)
          ================================================================= */}
      <section className="cloud-stage-hero">
        {/* Background Visual Asset */}
        <div className="cloud-stage-bg-art">
          <div className="cloud-stage-bg-plane">
            <img
              src={cloudHeroPoster}
              alt="GOLDLY AI 3D Cloud Core"
              className="cloud-stage-bg-img"
            />
          </div>
          <div className="cloud-stage-scrim"></div>
        </div>

        {/* Ambient Light Bloom */}
        <div className="cloud-stage-glow glow-one" aria-hidden="true"></div>
        <div className="cloud-stage-glow glow-two" aria-hidden="true"></div>
        <QuantumField />

        {/* Floating Brand Elements on Top of Visual (Zero Data, Pure Prestige) */}
        <div className="cloud-stage-overlay">
          {/* Top Brand Header */}
          <div className="cloud-stage-brand">
            <div className="cloud-stage-brand-text">
              <span className="cloud-stage-brand-name">GOLDLY AI</span>
              <span className="cloud-stage-brand-kicker">{t.brandKicker}</span>
            </div>
          </div>

          {/* Slogan at Bottom of Visual */}
          <div className="cloud-stage-caption">
            <h1 className="cloud-stage-headline">{t.stageTitle}</h1>
            <p className="cloud-stage-subhead">{t.stageSubtitle}</p>
          </div>
        </div>
      </section>

      {/* =================================================================
          RIGHT COLUMN: PRODUCT-GRADE ENTERPRISE AUTH PANEL (丰富多维、精致高级)
          ================================================================= */}
      <section className="cloud-auth-panel-pane">
        {/* Top Actions */}
        <div className="cloud-pane-topbar">
          <button
            type="button"
            className="cloud-pane-back"
            onClick={() => onBack?.("page-top")}
          >
            <Icon name="chevronLeft" size={14} />
            <span>{t.backHome}</span>
          </button>

          <button
            type="button"
            className="cloud-pane-lang"
            onClick={toggleLang}
            title={lang === "zh" ? "Switch to English" : "切换为中文"}
          >
            <Icon name="globe" size={14} />
            <span>{t.langName}</span>
          </button>
        </div>

        {/* Auth Card Center */}
        <div className="cloud-pane-center">
          <div className="cloud-pane-card">
            {/* Header */}
            <div className="cloud-pane-card-header">
              <h2>{t.welcome}</h2>
              <p>{t.welcomeSub}</p>
            </div>

            {/* Interactive Tabs: Password & Enterprise SSO */}
            <div className="cloud-auth-tabs" role="tablist">
              <button
                type="button"
                className={`cloud-tab-btn ${activeTab === "password" ? "active" : ""}`}
                onClick={() => { setActiveTab("password"); setMessage(""); }}
              >
                <Icon name="lock" size={14} />
                <span>{t.tabPassword}</span>
              </button>
              <button
                type="button"
                className={`cloud-tab-btn ${activeTab === "sso" ? "active" : ""}`}
                onClick={() => { setActiveTab("sso"); setMessage(""); }}
              >
                <Icon name="building" size={14} />
                <span>{t.tabSso}</span>
              </button>
            </div>

            {/* TAB 1: Password Form */}
            {activeTab === "password" && (
              <form onSubmit={submitLogin} noValidate className="cloud-pane-form">
                {/* Work Email */}
                <div className="cloud-pane-field">
                  <label htmlFor="cloud-email">{t.emailLabel}</label>
                  <div className="cloud-pane-input-box">
                    <span className="cloud-pane-icon">
                      <Icon name="mail" size={16} />
                    </span>
                    <input
                      id="cloud-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.emailPlaceholder}
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="cloud-pane-field">
                  <div className="cloud-pane-field-head">
                    <label htmlFor="cloud-password">{t.passwordLabel}</label>
                    <button
                      type="button"
                      className="cloud-pane-forgot"
                      onClick={showComingSoon}
                    >
                      {t.forgotPassword}
                    </button>
                  </div>
                  <div className="cloud-pane-input-box">
                    <span className="cloud-pane-icon">
                      <Icon name="lock" size={16} />
                    </span>
                    <input
                      id="cloud-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t.passwordPlaceholder}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="cloud-pane-pwd-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t.hidePassword : t.showPassword}
                    >
                      <Icon name={showPassword ? "eyeOff" : "eye"} size={16} />
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div className="cloud-pane-options">
                  <label className="cloud-pane-check-label">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    <span>{t.rememberMe}</span>
                  </label>
                </div>

                {/* Message */}
                {message && (
                  <div className="cloud-pane-alert" role="status">
                    <Icon name="info" size={16} className="cloud-alert-icon" />
                    <span>{message}</span>
                  </div>
                )}

                {/* Submit */}
                <button type="submit" className="cloud-pane-submit-btn">
                  <span>{t.submitBtn}</span>
                  <Icon name="arrowRight" size={16} className="cloud-btn-arrow" />
                </button>
              </form>
            )}

            {/* TAB 2: SSO Form */}
            {activeTab === "sso" && (
              <form onSubmit={submitLogin} noValidate className="cloud-pane-form">
                <div className="cloud-pane-field">
                  <label htmlFor="cloud-sso-domain">{t.ssoDomainLabel}</label>
                  <div className="cloud-pane-input-box">
                    <span className="cloud-pane-icon">
                      <Icon name="building" size={16} />
                    </span>
                    <input
                      id="cloud-sso-domain"
                      type="text"
                      value={ssoDomain}
                      onChange={(e) => setSsoDomain(e.target.value)}
                      placeholder={t.ssoDomainPlaceholder}
                    />
                  </div>
                </div>

                {message && (
                  <div className="cloud-pane-alert" role="status">
                    <Icon name="info" size={16} className="cloud-alert-icon" />
                    <span>{message}</span>
                  </div>
                )}

                <button type="submit" className="cloud-pane-submit-btn">
                  <span>{t.submitSsoBtn}</span>
                  <Icon name="arrowRight" size={16} className="cloud-btn-arrow" />
                </button>
              </form>
            )}

            {/* Footer Registration Link */}
            <div className="cloud-pane-new-account">
              <span>{t.noAccount}</span>
              <button type="button" onClick={showComingSoon}>
                {t.contactSales}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="cloud-pane-footer">
          <div className="cloud-pane-links">
            <button type="button" onClick={showComingSoon}>{t.terms}</button>
            <span>·</span>
            <button type="button" onClick={showComingSoon}>{t.privacy}</button>
            <span>·</span>
            <button type="button" onClick={showComingSoon}>{t.help}</button>
          </div>
          <div className="cloud-pane-copyright">{t.copyright}</div>
        </footer>
      </section>
    </main>
  );
};
